import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type { MutableRefObject } from 'react';
import type { Location, NavigateFunction } from 'react-router-dom';
import {
  createThreadControllerState,
  threadControllerReducer,
} from '../threading/controller';
import { syncThreadInUrl } from '../threading/url-sync';

type PendingSource = 'new_conversation' | 'sidebar_select' | 'url_nav' | null;

export interface UseThreadControllerOptions {
  searchParams: URLSearchParams;
  navigate: NavigateFunction;
  location: Location;
  isLoadingRef: MutableRefObject<boolean>;
  canLoadThreadMessages: boolean;
  loadThreadMessages: (threadId: string) => Promise<boolean>;
}

export interface UseThreadControllerResult {
  currentThreadId: string | null;
  phase: 'idle' | 'creating' | 'active' | 'switching';
  pendingSource: PendingSource;
  noActiveThread: boolean;
  onBeforeSend: () => void;
  onThreadCreated: (threadId: string) => void;
  startNewConversation: () => void;
  selectThread: (threadId: string) => void;
}

export function isCreationThreadSync(
  urlThreadId: string,
  creationThreadId: string | null,
  isLoading: boolean,
  pendingSource: PendingSource
): boolean {
  return creationThreadId === urlThreadId && (isLoading || pendingSource === 'new_conversation');
}

export function shouldLoadThreadMessages(
  lastLoadedThreadId: string | null,
  urlThreadId: string,
  isLoading: boolean,
  lastFailedThreadId: string | null = null
): boolean {
  if (isLoading) return false;
  if (lastLoadedThreadId === urlThreadId) return false;
  if (lastFailedThreadId === urlThreadId) return false;
  return true;
}

export function useThreadController({
  searchParams,
  navigate,
  location,
  isLoadingRef,
  canLoadThreadMessages,
  loadThreadMessages,
}: UseThreadControllerOptions): UseThreadControllerResult {
  const urlThreadId = searchParams.get('thread');
  const [state, dispatch] = useReducer(threadControllerReducer, urlThreadId, createThreadControllerState);

  // Start with null so refresh on ?thread=<id> performs initial thread history load.
  const lastLoadedThreadIdRef = useRef<string | null>(null);
  const lastFailedThreadIdRef = useRef<string | null>(null);
  const creationThreadIdRef = useRef<string | null>(null);
  const pendingSourceRef = useRef<PendingSource>(state.pendingSource);

  useEffect(() => {
    pendingSourceRef.current = state.pendingSource;
  }, [state.pendingSource]);

  const setThreadQueryParam = useCallback((threadId: string | null, replace: boolean = true) => {
    syncThreadInUrl(navigate, location.pathname, searchParams, threadId, replace);
  }, [navigate, location.pathname, searchParams]);

  const startNewConversation = useCallback(() => {
    creationThreadIdRef.current = null;
    lastLoadedThreadIdRef.current = null;
    lastFailedThreadIdRef.current = null;
    dispatch({ type: 'START_NEW_CONVERSATION' });
    setThreadQueryParam(null, true);
  }, [setThreadQueryParam]);

  const onBeforeSend = useCallback(() => {
    dispatch({ type: 'BEFORE_SEND' });
  }, []);

  const onThreadCreated = useCallback((threadId: string) => {
    creationThreadIdRef.current = threadId;
    lastLoadedThreadIdRef.current = threadId;
    lastFailedThreadIdRef.current = null;
    dispatch({ type: 'THREAD_CREATED', threadId });
    setThreadQueryParam(threadId, true);
  }, [setThreadQueryParam]);

  const selectThread = useCallback((threadId: string) => {
    dispatch({ type: 'SELECT_THREAD', threadId, source: 'sidebar_select' });
    setThreadQueryParam(threadId, false);
  }, [setThreadQueryParam]);

  useEffect(() => {
    const sync = async () => {
      // Wait for auth/bootstrap readiness before thread history fetches.
      if (!canLoadThreadMessages) return;

      if (urlThreadId) {
        dispatch({ type: 'SYNC_URL_THREAD', threadId: urlThreadId });

        const isCreationThread = isCreationThreadSync(
          urlThreadId,
          creationThreadIdRef.current,
          isLoadingRef.current,
          pendingSourceRef.current
        );
        if (isCreationThread) {
          dispatch({ type: 'THREAD_LOAD_SUCCESS', threadId: urlThreadId });
          return;
        }

        const shouldLoad = shouldLoadThreadMessages(
          lastLoadedThreadIdRef.current,
          urlThreadId,
          isLoadingRef.current,
          lastFailedThreadIdRef.current
        );
        if (shouldLoad) {
          const ok = await loadThreadMessages(urlThreadId);
          if (ok) {
            lastLoadedThreadIdRef.current = urlThreadId;
            lastFailedThreadIdRef.current = null;
            dispatch({ type: 'THREAD_LOAD_SUCCESS', threadId: urlThreadId });
          } else {
            lastLoadedThreadIdRef.current = null;
            lastFailedThreadIdRef.current = urlThreadId;
            dispatch({ type: 'THREAD_LOAD_FAILED' });
            setThreadQueryParam(null, true);
          }
        } else if (lastLoadedThreadIdRef.current === urlThreadId) {
          dispatch({ type: 'THREAD_LOAD_SUCCESS', threadId: urlThreadId });
        }
        return;
      }

      lastLoadedThreadIdRef.current = null;
      lastFailedThreadIdRef.current = null;
      dispatch({ type: 'SYNC_URL_THREAD', threadId: null });
    };

    void sync();
  }, [
    canLoadThreadMessages,
    urlThreadId,
    loadThreadMessages,
    isLoadingRef,
    setThreadQueryParam,
  ]);

  return useMemo(
    () => ({
      currentThreadId: state.activeThreadId,
      phase: state.phase,
      pendingSource: state.pendingSource,
      noActiveThread: state.threadless,
      onBeforeSend,
      onThreadCreated,
      startNewConversation,
      selectThread,
    }),
    [state, onBeforeSend, onThreadCreated, startNewConversation, selectThread]
  );
}

